import clsx from 'clsx'
import { useState } from 'react'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
export type AvatarStatus = 'none' | 'online' | 'company' | 'verified'

export interface AvatarProps {
    size?: AvatarSize
    src?: string
    alt?: string
    initials?: string
    status?: AvatarStatus
    companySrc?: string
    companyAlt?: string
    ring?: boolean
    className?: string
}

const SIZE_CLASSES: Record<AvatarSize, string> = {
    xs: 'size-6',
    sm: 'size-8',
    md: 'size-10',
    lg: 'size-12',
    xl: 'size-14',
    '2xl': 'size-16',
}

const RING_PADDING_CLASSES: Record<AvatarSize, string> = {
    xs: 'p-px',
    sm: 'p-px',
    md: 'p-px',
    lg: 'p-[1.5px]',
    xl: 'p-spacing-xxs',
    '2xl': 'p-spacing-xxs',
}

const INITIALS_CLASSES: Record<AvatarSize, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-md',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-display-xs',
}

const PLACEHOLDER_ICON_CLASSES: Record<AvatarSize, string> = {
    xs: 'size-3',
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-6',
    xl: 'size-7',
    '2xl': 'size-8',
}

const PLACEHOLDER_STROKE: Record<AvatarSize, number> = {
    xs: 2,
    sm: 2,
    md: 2,
    lg: 2.33,
    xl: 2.67,
    '2xl': 2.67,
}

const STATUS_DOT_CLASSES: Record<AvatarSize, string> = {
    xs: 'size-1.5',
    sm: 'size-2',
    md: 'size-2.5',
    lg: 'size-3',
    xl: 'size-3.5',
    '2xl': 'size-4',
}

const STATUS_BADGE_CLASSES: Record<AvatarSize, string> = {
    xs: 'size-2.5',
    sm: 'size-3',
    md: 'size-3.5',
    lg: 'size-4',
    xl: 'size-[18px]',
    '2xl': 'size-5',
}

const STATUS_LABELS: Record<AvatarStatus, string> = {
    none: '',
    online: ', online',
    company: '',
    verified: ', verified',
}

function VerifiedSeal({ size }: { size: AvatarSize }) {
    return (
        <svg
            viewBox="0 0 32 32"
            fill="none"
            className={clsx('text-foreground-fg-brand-tertiary', STATUS_BADGE_CLASSES[size])}
            aria-hidden="true">
            <path
                fill="currentColor"
                d="M24.7108 5.66727C25.0148 6.4023 25.5981 6.98655 26.3327 7.29157L28.9085 8.3585C29.6436 8.66298 30.2276 9.247 30.5321 9.98208C30.8366 10.7172 30.8366 11.5431 30.5321 12.2782L29.4659 14.8521C29.1613 15.5875 29.1609 16.4143 29.4669 17.1493L30.5312 19.7225C30.6821 20.0866 30.7598 20.4768 30.7599 20.871C30.76 21.2651 30.6824 21.6554 30.5316 22.0195C30.3807 22.3836 30.1596 22.7145 29.8809 22.9931C29.6022 23.2718 29.2713 23.4927 28.9071 23.6435L26.3331 24.7096C25.5981 25.0135 25.0139 25.5969 24.7088 26.3315L23.6419 28.9073C23.3374 29.6423 22.7534 30.2264 22.0183 30.5308C21.2832 30.8353 20.4573 30.8353 19.7222 30.5308L17.1483 29.4647C16.4132 29.161 15.5876 29.1616 14.8529 29.4664L12.2771 30.5318C11.5425 30.8356 10.7173 30.8353 9.98276 30.5311C9.24828 30.2269 8.66458 29.6435 8.35989 28.9092L7.29265 26.3327C6.98873 25.5977 6.40536 25.0134 5.67079 24.7084L3.09501 23.6415C2.36025 23.3371 1.7764 22.7535 1.47181 22.0188C1.16721 21.2841 1.1668 20.4586 1.47066 19.7236L2.53681 17.1497C2.84055 16.4146 2.83993 15.5889 2.53509 14.8543L1.47046 12.2767C1.31952 11.9126 1.2418 11.5223 1.24174 11.1282C1.24167 10.7341 1.31927 10.3438 1.4701 9.97969C1.62092 9.61556 1.84202 9.28472 2.12075 9.00608C2.39949 8.72743 2.7304 8.50644 3.09457 8.35573L5.6685 7.28957C6.40289 6.98592 6.98679 6.40328 7.29204 5.66956L8.35896 3.09378C8.66344 2.3587 9.24747 1.77467 9.98255 1.47019C10.7176 1.16571 11.5436 1.16571 12.2787 1.47019L14.8526 2.53635C15.5877 2.84008 16.4133 2.83946 17.148 2.53462L19.7248 1.47185C20.4598 1.16753 21.2856 1.1676 22.0205 1.47202C22.7554 1.77644 23.3394 2.3603 23.6439 3.09519L24.7112 5.67173L24.7108 5.66727Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                className="fill-base-white"
                d="M22.2665 11.8058C22.4803 11.4701 22.5519 11.0631 22.4656 10.6746C22.3794 10.286 22.1423 9.94759 21.8065 9.73383C21.4708 9.52006 21.0638 9.44843 20.6753 9.5347C20.2867 9.62097 19.9483 9.85806 19.7345 10.1938L13.8605 19.4238L11.1725 16.0638C10.924 15.753 10.5622 15.5536 10.1667 15.5095C9.77118 15.4655 9.37436 15.5803 9.06352 15.8288C8.75269 16.0773 8.55331 16.4391 8.50923 16.8347C8.46516 17.2302 8.58001 17.627 8.82852 17.9378L12.8285 22.9378C12.9776 23.1244 13.1691 23.2727 13.387 23.3703C13.6049 23.468 13.843 23.5122 14.0815 23.4993C14.32 23.4864 14.5519 23.4167 14.758 23.2961C14.9641 23.1755 15.1385 23.0074 15.2665 22.8058L22.2665 11.8058Z"
            />
        </svg>
    )
}

function UserPlaceholder({ size }: { size: AvatarSize }) {
    return (
        <svg
            className={clsx('text-text-quaternary', PLACEHOLDER_ICON_CLASSES[size])}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={PLACEHOLDER_STROKE[size]}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
        </svg>
    )
}

function CompanyBadge({ size, src, alt }: { size: AvatarSize; src?: string; alt: string }) {
    return (
        <span
            className={clsx(
                'absolute -bottom-0.5 -right-0.5 overflow-hidden rounded-radius-full border-[1.5px] border-solid border-base-white bg-background-bg-tertiary',
                STATUS_BADGE_CLASSES[size],
            )}>
            {src ? (
                <img src={src} alt={alt} className="size-full object-cover" />
            ) : (
                <svg
                    className="size-full p-spacing-xxs text-text-quaternary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                </svg>
            )}
        </span>
    )
}

export const Avatar = ({
    size = 'md',
    src,
    alt = '',
    initials,
    status = 'none',
    companySrc,
    companyAlt = '',
    ring = false,
    className,
}: AvatarProps) => {
    const [imageError, setImageError] = useState(false)

    const hasImage = Boolean(src) && !imageError
    const hasInitials = !hasImage && Boolean(initials)
    const isPlaceholder = !hasImage && !hasInitials

    const accessibleName = alt || initials || 'Avatar'

    return (
        <span
            role="img"
            aria-label={`${accessibleName}${STATUS_LABELS[status]}`}
            className={clsx(
                'relative inline-flex shrink-0 rounded-radius-full font-body',
                SIZE_CLASSES[size],
                ring && 'bg-background-bg-primary shadow-xs ring-1 ring-alpha-black-10',
                ring && RING_PADDING_CLASSES[size],
                className,
            )}>
            <span
                className={clsx(
                    'relative flex size-full items-center justify-center overflow-hidden rounded-radius-full border border-solid',
                    hasImage
                        ? 'border-alpha-black-10'
                        : 'border-border-secondary bg-background-bg-tertiary',
                )}>
                {hasImage && (
                    <img
                        src={src}
                        alt={alt}
                        className="absolute inset-0 size-full rounded-radius-full object-cover"
                        onError={() => setImageError(true)}
                    />
                )}

                {hasInitials && (
                    <span
                        className={clsx(
                            'font-semibold text-text-quaternary',
                            INITIALS_CLASSES[size],
                        )}>
                        {initials}
                    </span>
                )}

                {isPlaceholder && <UserPlaceholder size={size} />}
            </span>

            {status === 'online' && (
                <span
                    className={clsx(
                        'absolute -bottom-px -right-px rounded-radius-full border-[1.5px] border-solid border-base-white bg-foreground-fg-success-secondary',
                        STATUS_DOT_CLASSES[size],
                    )}
                />
            )}

            {status === 'company' && <CompanyBadge size={size} src={companySrc} alt={companyAlt} />}

            {status === 'verified' && (
                <span className="absolute -bottom-0.5 -right-0.5 flex">
                    <VerifiedSeal size={size} />
                </span>
            )}
        </span>
    )
}
